import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Ear, Headphones, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200" data-testid="header">
      <div className="container-width">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-[#0A192F] flex items-center justify-center transition-transform group-hover:scale-105">
              <span className="text-white font-bold text-base lg:text-lg font-display">SS</span>
            </div>
            <div className="hidden sm:block">
              <div className="font-display font-semibold text-[#0A192F] text-sm lg:text-base">Dr. Sanjay Sachdeva</div>
              <div className="text-xs text-[#1E40AF] font-medium">ENT & Cochlear Implant Surgeon</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            <Link 
              to="/" 
              className={`nav-link ${isActive('/') && location.pathname === '/' ? 'text-[#1E40AF] bg-blue-50' : ''}`}
              data-testid="nav-home"
            >
              Home
            </Link>
            
            <Link 
              to="/cochlear-implant" 
              className={`nav-link font-semibold flex items-center gap-1.5 ${isActive('/cochlear-implant') ? 'text-[#1E40AF] bg-blue-50' : 'text-[#1E40AF]'}`}
              data-testid="nav-cochlear-implant"
            >
              <Ear className="w-4 h-4" strokeWidth={1.5} />
              Cochlear Implants
            </Link>

            <Link 
              to="/cochlear-implant/products" 
              className={`nav-link ${location.pathname === '/cochlear-implant/products' ? 'text-[#1E40AF] bg-blue-50' : ''}`}
              data-testid="nav-ci-products"
            >
              CI Products
            </Link>

            <Link 
              to="/hearing-aids" 
              className={`nav-link font-semibold flex items-center gap-1.5 ${isActive('/hearing-aids') ? 'text-emerald-600 bg-emerald-50' : 'text-emerald-600'}`}
              data-testid="nav-hearing-aids"
            >
              <Headphones className="w-4 h-4" strokeWidth={1.5} />
              Hearing Aids
            </Link>
            
            <Link 
              to="/videos" 
              className={`nav-link ${isActive('/videos') ? 'text-[#1E40AF] bg-blue-50' : ''}`}
              data-testid="nav-videos"
            >
              Videos
            </Link>
            
            <Link 
              to="/contact" 
              className={`nav-link ${isActive('/contact') ? 'text-[#1E40AF] bg-blue-50' : ''}`}
              data-testid="nav-contact"
            >
              Contact
            </Link>
            
            <Link 
              to="/csr" 
              className={`nav-link font-semibold ${isActive('/csr') ? 'text-rose-600 bg-rose-50' : 'text-rose-600'}`}
              data-testid="nav-csr"
            >
              Support Deaf Children
            </Link>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a 
              href="tel:+919910317286" 
              className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-[#1E40AF] transition-colors"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" strokeWidth={1.5} />
              <span className="hidden xl:inline">9910317286</span>
            </a>
            <Link to="/book-appointment">
              <Button 
                className="bg-[#0A192F] hover:bg-[#1E293B] text-white font-semibold rounded-full shadow-md transition-transform hover:scale-105"
                data-testid="book-appointment-btn"
              >
                <Calendar className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Book Appointment
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-100 bg-white" data-testid="mobile-menu">
            <nav className="space-y-1">
              <Link 
                to="/" 
                className="block px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/cochlear-implant" 
                className="block px-4 py-3 font-semibold text-[#1E40AF] hover:bg-blue-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Ear className="w-4 h-4 inline mr-2" strokeWidth={1.5} />
                Cochlear Implants
              </Link>
              <Link 
                to="/cochlear-implant/products" 
                className="block px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg pl-10"
                onClick={() => setMobileMenuOpen(false)}
              >
                CI Products & Pricing
              </Link>
              <Link 
                to="/hearing-aids" 
                className="block px-4 py-3 font-semibold text-emerald-600 hover:bg-emerald-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Headphones className="w-4 h-4 inline mr-2" strokeWidth={1.5} />
                Hearing Aids
              </Link>
              <Link 
                to="/videos" 
                className="block px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Videos
              </Link>
              <Link 
                to="/contact" 
                className="block px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact
              </Link>
              <Link 
                to="/csr" 
                className="block px-4 py-3 font-semibold text-rose-600 hover:bg-rose-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Support Deaf Children
              </Link>
              <div className="pt-4 px-4 space-y-3">
                <a href="tel:+919910317286" className="flex items-center gap-2 text-slate-600">
                  <Phone className="w-4 h-4" strokeWidth={1.5} />
                  <span>9910317286</span>
                </a>
                <Link to="/book-appointment" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-[#0A192F] hover:bg-[#1E293B] text-white font-semibold rounded-full">
                    <Calendar className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Book Appointment
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
